import {useMemo} from 'react';
import {useDocument} from 'react-firebase-hooks/firestore';

import {getShadowConfigDocRef} from '@cip/core/firebase/client';
import {resolveRequiredShadowReviewsPerWeek} from '@cip/core/section';
import {useCourseId} from 'hooks/router/useUrlParams';

import {
  useShadowAssignments,
  type ShadowAssignmentSummary,
} from './useShadowAssignments';

/** Return shape of {@link useSectionShadowRoadmapProgress}. */
export interface SectionShadowRoadmapProgress {
  /**
   * True once the course's shadowing config and the reviewer's assignments
   * have both been read. Stays false while either is loading, when the config
   * read fails, and when the hook is disabled.
   */
  isLoaded: boolean;
  /** The current reviewer's shadow assignments; empty until loaded. */
  assignments: ShadowAssignmentSummary[];
  /** The course's required reviews per week, resolved to its default. */
  requiredPerWeek: number;
}

/**
 * Reads what a roadmap "Section Shadow" item needs to decide completion (see
 * `isSectionShadowItemComplete`): the current reviewer's assignments and the
 * course's required reviews per week. Read-only and reactive.
 *
 * @param options.isEnabled - When false, nothing is read and `isLoaded` stays
 *   false. Omitted means the reads run.
 * @returns The reviewer's assignments, the per-week number, and whether both
 *   reads have landed.
 */
export function useSectionShadowRoadmapProgress({
  isEnabled = true,
}: {isEnabled?: boolean} = {}): SectionShadowRoadmapProgress {
  const courseId = useCourseId();
  const configRef = useMemo(
    () => (isEnabled && courseId ? getShadowConfigDocRef({courseId}) : null),
    [isEnabled, courseId],
  );
  const [configSnap, , configError] = useDocument(configRef);
  const {isLoading: isAssignmentsLoading, assignments} = useShadowAssignments({
    isEnabled,
  });

  if (configError) {
    console.error('[shadowing] roadmap config read failed', configError);
  }

  return {
    isLoaded: configSnap !== undefined && !isAssignmentsLoading,
    assignments,
    requiredPerWeek: resolveRequiredShadowReviewsPerWeek(
      configSnap?.data()?.requiredReviewsPerWeek,
    ),
  };
}
